import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ShoppingBag, Plus, Minus, Trash2, CheckCircle2, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/wolfApi";
import { STORE_OPEN, fmt } from "@/lib/storeConfig";
import { NotifyForm } from "@/components/merch/NotifyForm";
import { ProductCard } from "@/components/merch/ProductCard";
import { ProductModal } from "@/components/merch/ProductModal";
import { ShippingModal } from "@/components/merch/ShippingModal";
import { WolfPhillySection } from "@/components/merch/WolfPhillySection";

const CART_KEY = "wolf_cart_v1";

function loadCart() {
  try {
    return JSON.parse(localStorage.getItem(CART_KEY)) || [];
  } catch {
    return [];
  }
}

const CATEGORIES = [
  { id: "All", label: "All" },
  { id: "Tee", label: "Tees" },
  { id: "Hoodie", label: "Hoodies" },
  { id: "Cap", label: "Caps" },
  { id: "Accessory", label: "Accessories" },
  { id: "Premium", label: "Premium" },
  { id: "Jewelry", label: "Jewelry" },
];

export default function Merch() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);
  const [cart, setCart] = useState(loadCart);
  const [cartOpen, setCartOpen] = useState(false);
  const [shipOpen, setShipOpen] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [error, setError] = useState("");
  const [payState, setPayState] = useState(null); // null | "checking" | "paid" | "failed" | "expired"
  const loc = useLocation();
  const nav = useNavigate();

  useEffect(() => {
    api
      .get("/merch/products")
      .then((r) => setProducts(r.data.products || []))
      .catch(() => setError("Could not load the collection. Refresh to try again."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(cart));
  }, [cart]);

  // Payment return polling
  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    const sessionId = params.get("session_id");
    if (!sessionId) return;
    let cancelled = false;
    setPayState("checking");
    const poll = async (attempt) => {
      if (cancelled) return;
      if (attempt >= 8) {
        setPayState("failed");
        return;
      }
      try {
        const r = await api.get(`/merch/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (r.data.payment_status === "paid") {
          setCart([]);
          localStorage.removeItem(CART_KEY);
          setPayState("paid");
          return;
        }
        if (r.data.status === "expired") {
          setPayState("expired");
          return;
        }
        setTimeout(() => poll(attempt + 1), 2000);
      } catch {
        setTimeout(() => poll(attempt + 1), 2000);
      }
    };
    poll(0);
    return () => {
      cancelled = true;
    };
  }, [loc.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const dismissPayState = () => {
    setPayState(null);
    nav("/merch", { replace: true });
  };

  const visible = useMemo(
    () => (filter === "All" ? products : products.filter((p) => p.category === filter)),
    [products, filter]
  );
  const seasonItems = useMemo(() => visible.filter((p) => !p.premium), [visible]);
  const premiumItems = useMemo(() => visible.filter((p) => p.premium), [visible]);

  const cartCount = cart.reduce((n, i) => n + i.qty, 0);
  const cartTotal = useMemo(
    () =>
      cart.reduce((n, i) => {
        const p = products.find((x) => x.id === i.product_id);
        return n + (p ? p.price * i.qty : 0);
      }, 0),
    [cart, products]
  );

  const addToCart = (product, size) => {
    setCart((prev) => {
      const key = (i) => i.product_id === product.id && i.size === size;
      const existing = prev.find(key);
      if (existing) return prev.map((i) => (key(i) ? { ...i, qty: Math.min(i.qty + 1, 10) } : i));
      return [...prev, { product_id: product.id, size, qty: 1 }];
    });
    setActive(null);
    setCartOpen(true);
  };

  const setQty = (idx, qty) => {
    setCart((prev) =>
      qty <= 0 ? prev.filter((_, i) => i !== idx) : prev.map((it, i) => (i === idx ? { ...it, qty: Math.min(qty, 10) } : it))
    );
  };

  const checkout = async (shipping) => {
    if (!cart.length) return;
    setCheckingOut(true);
    setError("");
    try {
      const r = await api.post("/merch/checkout", {
        items: cart,
        origin_url: window.location.origin,
        shipping,
      });
      window.location.href = r.data.url;
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail));
      setCheckingOut(false);
      setShipOpen(false);
    }
  };

  return (
    <div className="pt-32 sm:pt-40 pb-20 bg-[#080808] min-h-screen" data-testid="merch-page">
      <div className="max-w-[1500px] mx-auto px-6 sm:px-10 lg:px-16">
        {/* HERO */}
        <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] mb-6">— Merch · Est. 1987</p>
        <h1 className="font-display wolf-gold-foil text-6xl sm:text-8xl lg:text-9xl leading-[0.85]">
          Season One
        </h1>
        <p className="font-editorial italic text-xl sm:text-2xl text-white/55 mt-6 max-w-2xl">
          Wear the story. Live the message. Join the Pack.
        </p>
        {!STORE_OPEN && (
          <div className="mt-8 max-w-xl">
            <p data-testid="merch-coming-soon-banner" className="inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] border border-[#D4AF37]/40 bg-[#7A5A18]/15 px-5 py-3">
              The store opens soon — preview the collection below
            </p>
            <NotifyForm testIdPrefix="merch-notify" className="mt-5" />
          </div>
        )}

        {/* FILTER + CART BAR */}
        <div className="mt-12 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex gap-3 flex-wrap">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                data-testid={`merch-filter-${c.id.toLowerCase()}`}
                onClick={() => setFilter(c.id)}
                className={`text-[11px] uppercase tracking-[0.28em] px-5 py-2.5 border transition-all ${
                  filter === c.id
                    ? "border-[#D4AF37] text-[#D4AF37] bg-[#7A5A18]/15"
                    : "border-white/15 text-white/55 hover:text-white hover:border-white/40"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
          {STORE_OPEN && (
          <button
            data-testid="merch-cart-button"
            onClick={() => setCartOpen(true)}
            className="relative inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.28em] text-white border border-[#D4AF37]/40 hover:border-[#D4AF37] bg-[#7A5A18]/15 hover:bg-[#7A5A18]/30 px-5 py-2.5 transition-all"
          >
            <ShoppingBag size={16} className="text-[#D4AF37]" />
            Cart
            {cartCount > 0 && (
              <span
                data-testid="merch-cart-count"
                className="inline-flex items-center justify-center min-w-[20px] h-5 px-1 text-[10px] font-bold bg-[#D4AF37] text-black rounded-full"
              >
                {cartCount}
              </span>
            )}
          </button>
          )}
        </div>

        {error && (
          <p data-testid="merch-error" className="mt-6 text-sm text-red-400/90 border border-red-400/30 bg-red-950/20 px-4 py-3">
            {error}
          </p>
        )}

        {/* GRID */}
        {loading ? (
          <div className="mt-16 flex items-center gap-3 text-white/50">
            <Loader2 className="animate-spin" size={18} /> Loading the collection…
          </div>
        ) : (
          <>
            <div className="mt-14 grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 sm:gap-8">
              {seasonItems.map((p, i) => (
                <ProductCard key={p.id} p={p} i={i} onClick={() => setActive(p)} />
              ))}
            </div>
            {premiumItems.length > 0 && (
              <section className="mt-24" data-testid="premium-collection-section">
                <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] mb-5">— Solid 14K Gold · Full Grain Leather · Built To Last</p>
                <h2 className="font-display wolf-gold-foil text-4xl sm:text-6xl leading-none">The Premium Collection</h2>
                <p className="font-editorial italic text-lg text-white/50 mt-4 max-w-xl">
                  More than accessories. Extensions of who you are.
                </p>
                <div className="mt-10 grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 sm:gap-8">
                  {premiumItems.map((p, i) => (
                    <ProductCard key={p.id} p={p} i={i} onClick={() => setActive(p)} />
                  ))}
                </div>
              </section>
            )}
          </>
        )}

        {/* WOLF & PHILLY VAPE SHOWCASE */}
        <WolfPhillySection />

        {/* BRAND STATEMENT */}
        <div className="mt-24 border-t border-white/10 pt-12 max-w-3xl">
          <p className="font-editorial italic text-2xl sm:text-3xl text-white/70 leading-relaxed">
            "This is more than clothing. It's a movement of faith, freedom and purpose.{" "}
            <span className="wolf-gold-foil font-display not-italic">Welcome to the Pack.</span>"
          </p>
        </div>
      </div>

      {/* PRODUCT MODAL */}
      <AnimatePresence>
        {active && <ProductModal product={active} onClose={() => setActive(null)} onAdd={addToCart} />}
      </AnimatePresence>

      {/* CART DRAWER */}
      <AnimatePresence>
        {cartOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm"
              onClick={() => setCartOpen(false)}
            />
            <motion.aside
              data-testid="merch-cart-drawer"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.3 }}
              className="fixed right-0 top-0 bottom-0 z-[80] w-full max-w-md bg-[#0a0a0a] border-l border-[#D4AF37]/25 flex flex-col"
            >
              <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
                <h2 className="font-display text-2xl text-[#D4AF37] tracking-wider">Your Cart</h2>
                <button data-testid="merch-cart-close" onClick={() => setCartOpen(false)} className="text-white/60 hover:text-white">
                  <X size={22} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                {cart.length === 0 && (
                  <p className="text-white/45 text-sm" data-testid="merch-cart-empty">
                    The cart is empty. The Pack travels light — but not this light.
                  </p>
                )}
                {cart.map((item, idx) => {
                  const p = products.find((x) => x.id === item.product_id);
                  if (!p) return null;
                  return (
                    <div key={`${item.product_id}-${item.size}`} className="flex gap-4 border border-white/8 bg-[#0d0d0d] p-3" data-testid={`merch-cart-item-${p.id}`}>
                      <img src={p.img} alt={p.name} className="w-20 h-20 object-cover bg-black" />
                      <div className="flex-1 min-w-0">
                        <p className="font-display text-white leading-tight">{p.name}</p>
                        <p className="text-[11px] text-white/50 mt-1">
                          {item.size ? `Size ${item.size} · ` : ""}{fmt(p.price)}
                        </p>
                        <div className="flex items-center gap-3 mt-2">
                          <button data-testid={`merch-cart-dec-${p.id}`} onClick={() => setQty(idx, item.qty - 1)} className="w-7 h-7 border border-white/20 text-white/70 hover:border-[#D4AF37] inline-flex items-center justify-center">
                            <Minus size={13} />
                          </button>
                          <span className="text-sm text-white w-5 text-center" data-testid={`merch-cart-qty-${p.id}`}>{item.qty}</span>
                          <button data-testid={`merch-cart-inc-${p.id}`} onClick={() => setQty(idx, item.qty + 1)} className="w-7 h-7 border border-white/20 text-white/70 hover:border-[#D4AF37] inline-flex items-center justify-center">
                            <Plus size={13} />
                          </button>
                          <button data-testid={`merch-cart-remove-${p.id}`} onClick={() => setQty(idx, 0)} className="ml-auto text-white/40 hover:text-red-400">
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="px-6 py-5 border-t border-white/10">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-[11px] uppercase tracking-[0.28em] text-white/55">Total</span>
                  <span className="font-display text-2xl text-[#D4AF37]" data-testid="merch-cart-total">{fmt(cartTotal)}</span>
                </div>
                <button
                  data-testid="merch-checkout-button"
                  disabled={!cart.length || checkingOut}
                  onClick={() => setShipOpen(true)}
                  className="w-full inline-flex items-center justify-center gap-2 text-[12px] uppercase tracking-[0.3em] font-semibold text-black bg-[#D4AF37] hover:bg-[#F4C542] disabled:opacity-40 disabled:cursor-not-allowed px-6 py-4 transition-all"
                >
                  Checkout
                </button>
                <p className="text-[10px] text-white/35 mt-3 text-center uppercase tracking-[0.2em]">Secure payment via Stripe · AUD</p>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* SHIPPING MODAL */}
      <AnimatePresence>
        {shipOpen && (
          <ShippingModal
            total={cartTotal}
            busy={checkingOut}
            onClose={() => !checkingOut && setShipOpen(false)}
            onSubmit={checkout}
          />
        )}
      </AnimatePresence>

      {/* PAYMENT STATUS OVERLAY */}
      <AnimatePresence>
        {payState && (
          <motion.div
            data-testid="merch-payment-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[90] bg-black/85 backdrop-blur-md flex items-center justify-center px-6"
          >
            <div className="max-w-md w-full bg-[#0a0a0a] border border-[#D4AF37]/30 p-10 text-center">
              {payState === "checking" && (
                <>
                  <Loader2 className="animate-spin text-[#D4AF37] mx-auto" size={40} />
                  <h2 className="font-display text-2xl text-white mt-6">Confirming your order…</h2>
                  <p className="text-sm text-white/50 mt-3">Hold tight. We're verifying the payment with Stripe.</p>
                </>
              )}
              {payState === "paid" && (
                <>
                  <CheckCircle2 className="text-[#D4AF37] mx-auto" size={44} />
                  <h2 className="font-display wolf-gold-foil text-3xl mt-6" data-testid="merch-payment-success">Order Confirmed</h2>
                  <p className="text-sm text-white/55 mt-3">Welcome to the Pack. Your gear is on the way — wear the story.</p>
                  <button data-testid="merch-payment-done" onClick={dismissPayState} className="mt-8 text-[11px] uppercase tracking-[0.3em] text-black bg-[#D4AF37] hover:bg-[#F4C542] px-8 py-3 transition-all">
                    Back to the Shop
                  </button>
                </>
              )}
              {(payState === "failed" || payState === "expired") && (
                <>
                  <X className="text-red-400 mx-auto" size={44} />
                  <h2 className="font-display text-2xl text-white mt-6" data-testid="merch-payment-failed">
                    {payState === "expired" ? "Session Expired" : "Payment Not Confirmed"}
                  </h2>
                  <p className="text-sm text-white/50 mt-3">
                    {payState === "expired"
                      ? "The checkout session expired. Your cart is still saved — try again."
                      : "We couldn't confirm the payment. If you were charged, check your email for a Stripe receipt."}
                  </p>
                  <button data-testid="merch-payment-close" onClick={dismissPayState} className="mt-8 text-[11px] uppercase tracking-[0.3em] text-white border border-white/25 hover:border-white px-8 py-3 transition-all">
                    Close
                  </button>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
